import { fetchSyllabus } from '@/fetchers'
import {
	Syllabus,
	SyllabusAssessmentStagecontent,
} from '@/kontent/content-types'
import CustomAccordion from '@/legacy-ported/components/custom/CustomAccordion'
import CoursePerformance from '@/legacy-ported/components/syllabus/CoursePerformance'
import { Mapping, TaxoStageWithLifeSkill } from '@/types'
import { getSyllabusElements } from '@/utils'
import {
	Elements,
	IContentItemsContainer,
	Responses,
} from '@kontent-ai/delivery-sdk'
import { useQuery } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { Fragment, useState } from 'react'
import { Loading } from '../Loading'
import { getLinkedItems } from '../contexts/KontentHomeConfigProvider'
import { GridCol } from '../nsw/grid/GridCol'
import { GridWrapper } from '../nsw/grid/GridWrapper'
import { SideNav, SideNavItem } from '../nsw/side-nav/SideNav'

export interface SyllabusAssessmentProps {
	syllabus: Syllabus
	linkedItems: IContentItemsContainer
	mappings: Mapping[]
	currentStages: TaxoStageWithLifeSkill[]
	syllabusResponse?: Responses.IViewContentItemResponse<Syllabus>
	lazy?: boolean
}

type AssessmentSetType =
	| 'Course standards'
	| 'School-based assessment'
	| 'HSC examinations'

interface AssessmentSet {
	assessmentsInfo: Elements.RichTextElement
	assessmentStageContents?: Elements.LinkedItemsElement<SyllabusAssessmentStagecontent>
	type?: AssessmentSetType
}

const getAssessmentContents = (syllabus: Syllabus) => {
	const assessmentSets: AssessmentSet[] = [
		{
			assessmentsInfo: syllabus.elements.assessments_info,
			assessmentStageContents: syllabus.elements.assessments,
		},
		{
			assessmentsInfo: syllabus.elements.cs_assessments_info,
			assessmentStageContents: syllabus.elements.cs_assessments,
			type: 'Course standards',
		},
		{
			assessmentsInfo: syllabus.elements.sb_assessments_info,
			assessmentStageContents: syllabus.elements.sb_assessments,
			type: 'School-based assessment',
		},
		{
			assessmentsInfo: syllabus.elements.hsc_assessments_info,
			assessmentStageContents: syllabus.elements.hsc_assessments,
			type: 'HSC examinations',
		},
	]
	return assessmentSets
}

const getStageContentFromAssessmentSet = (
	assessmentSet: AssessmentSet,
	currentStages: TaxoStageWithLifeSkill[],
	linkedItems: IContentItemsContainer,
) =>
	getLinkedItems(assessmentSet.assessmentStageContents, linkedItems)?.filter(
		(assessment) => {
			return assessment.elements.stages__stages.value?.some((stage) =>
				currentStages.includes(stage.codename),
			)
		},
	) || []

const AssessmentContentV1 = ({
	syllabus,
	linkedItems,
	mappings,
	currentStages,
	hasHash,
}: {
	syllabus: Syllabus
	linkedItems: IContentItemsContainer
	mappings: Mapping[]
	currentStages: TaxoStageWithLifeSkill[]
	hasHash: boolean
}) => {
	const assessmentSets = getAssessmentContents(syllabus)

	return (
		<>
			{assessmentSets.map((assessmentSet) => {
				const sections = getStageContentFromAssessmentSet(
					assessmentSet,
					currentStages,
					linkedItems,
				)

				const isAccordion = assessmentSet.type && sections.length

				const Component = isAccordion ? CustomAccordion : Fragment

				return (
					<Component
						key={assessmentSet.type}
						title={isAccordion ? assessmentSet.type : undefined}
						expanded={isAccordion && hasHash ? hasHash : undefined}
					>
						<CoursePerformance
							info={assessmentSet.assessmentsInfo}
							sections={sections}
							mappings={mappings}
							linkedItems={linkedItems}
							suffixForHeading={syllabus.system.codename}
						/>
					</Component>
				)
			})}
		</>
	)
}

const AssessmentContentV2 = ({
	syllabus,
	linkedItems,
	mappings,
	currentStages,
	syllabusResponse,
}: {
	syllabus: Syllabus
	linkedItems: IContentItemsContainer
	mappings: Mapping[]
	currentStages: TaxoStageWithLifeSkill[]
	syllabusResponse: Responses.IViewContentItemResponse<Syllabus>
}) => {
	const assessmentPages = getLinkedItems(
		syllabus.elements.assessments_pages,
		syllabusResponse.linkedItems,
	)
	const [section, setSection] = useState<string | null>(
		assessmentPages[0].elements.slug.value,
	)

	const staticSideNavItems: SideNavItem[] = assessmentPages.map((item) => ({
		text: item.elements.title.value,
		href: '#',
		isActive: item.elements.slug.value === section,
		onClick: (e) => {
			e.preventDefault()
			setSection(item.elements.slug.value)
		},
	}))

	const currentSectionTitle = staticSideNavItems?.find(
		(item) => item.isActive,
	)?.text

	const assessmentPage = assessmentPages.find(
		(item) => item.elements.slug.value === section,
	)

	const assessmentStageContents =
		getLinkedItems(
			assessmentPage?.elements.assessments,
			syllabusResponse.linkedItems,
		)?.filter((item) => {
			return item.elements.stages__stages.value?.some((stage) =>
				currentStages.includes(stage.codename),
			)
		}) || []

	return (
		<>
			<GridWrapper className="-mt-8">
				<GridCol lg={3}>
					<SideNav className="block" items={staticSideNavItems} />
				</GridCol>
				<GridCol lg={9}>
					<h3 className="mb-8">{currentSectionTitle}</h3>
					<CoursePerformance
						info={assessmentPage?.elements.info}
						sections={assessmentStageContents}
						mappings={mappings}
						linkedItems={syllabusResponse.linkedItems}
						suffixForHeading={syllabus.system.codename}
						isUseNonFullWidthWrapper={false}
						sectionTitleTag={'h4'}
						assessmentGroupTitleTag={'h5'}
						contentAfterSections={assessmentPage?.elements.content}
					/>
				</GridCol>
			</GridWrapper>
		</>
	)
}

export const SyllabusAssessment = ({
	syllabus,
	linkedItems,
	mappings,
	currentStages,
	syllabusResponse: _syllabusResponse,
	lazy = true,
}: SyllabusAssessmentProps) => {
	const router = useRouter()
	const hasHash = router.asPath.includes('#')
	const syllabusCodename = syllabus.system.codename

	const { data: syllabusResponse, isFetched } = useQuery(
		[
			`SyllabusAssessment${syllabusCodename + lazy ? 'lazy' : ''}`,
			syllabusCodename,
		],
		() => {
			return lazy
				? fetchSyllabus(
						syllabusCodename,
						getSyllabusElements(['assessment']),
						5,
				  )
				: _syllabusResponse
		},
		{
			staleTime: Infinity,
		},
	)

	if (!isFetched) return <Loading />

	const _linkedItems = {
		...linkedItems,
		...syllabusResponse.linkedItems,
	}

	const isAssessmentModelV2 =
		syllabus.elements.assessment_model?.value?.[0]?.codename === 'v2'

	return isAssessmentModelV2 ? (
		<AssessmentContentV2
			syllabus={syllabus}
			linkedItems={_linkedItems}
			mappings={mappings}
			currentStages={currentStages}
			syllabusResponse={syllabusResponse}
		/>
	) : (
		<AssessmentContentV1
			syllabus={syllabus}
			linkedItems={_linkedItems}
			mappings={mappings}
			currentStages={currentStages}
			hasHash={hasHash}
		/>
	)
}
